import { useHydrated } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
	Header,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
} from "react-aria-components";
import { cn, tv } from "tailwind-variants";
import type { LanguageDto } from "#/_generated/umbraco/delivery-api/models";
import { Button } from "#/_packages/umbrella/features/button/components/button.tsx";
import UmbSpriteIcon, {
	type SpriteIconSize,
} from "#/_packages/umbrella/features/icons/components/umb-sprite-icon.tsx";
import type { ButtonVariant, Intent, Size } from "#/_packages/umbrella/types";
import { trackLanguageSelection } from "#/_packages/umbrella/utils/link-tracking";
import { m } from "#/paraglide/messages";
import { getLocale } from "#/paraglide/runtime";
import {
	GOOGLE_TRANSLATE_CHANGE_EVENT,
	getGoogleTranslateSelect,
	selectedGoogleTranslateLanguage,
} from "../utils/google-translate-loader";

export type LanguageTextLabelVariant = "none" | "abbreviation" | "text";
export type LanguageIconPosition = "start" | "end";

export type LanguageItem = {
	code: string;
	label: string;
	abbreviation: string;
	href: string;
	isActive?: boolean;
};

export type LanguageSelectorProps = {
	languages: readonly LanguageDto[];
	textLabelVariant?: LanguageTextLabelVariant;
	size?: Size;
	intentColor?: Intent;
	typeVariant?: ButtonVariant;
	icon?: string | null;
	iconPosition?: LanguageIconPosition;
	iconSize?: SpriteIconSize;
	className?: string;
};

const languageSelector = tv({
	slots: {
		root: "block__language-selector dropdown",
		button:
			"btn-language-selector inline-flex! items-center gap-1 dropdown-toggle capitalize",
		menu: "dropdown-menu dropdown-language-selector dropdown-menu-end bg-white",
		item: "dropdown-item w-full outline-none",
	},
	variants: {
		size: {
			extraSmall: { button: "btn-xs" },
			small: { button: "btn-sm" },
			medium: { button: "" },
			large: { button: "btn-lg" },
			extraLarge: { button: "btn-xl" },
			fullWidth: { button: "btn-full-width" },
		},
		intentColor: {
			primary: { button: "btn-primary" },
			secondary: { button: "btn-secondary" },
			light: { button: "btn-light" },
			dark: { button: "btn-dark" },
			white: { button: "btn-white" },
			black: { button: "btn-black" },
			success: { button: "btn-success" },
			warning: { button: "btn-warning" },
			danger: { button: "btn-danger" },
			info: { button: "btn-info" },
			none: { button: "btn-none" },
		},
		typeVariant: {
			solid: { button: "" },
			outline: { button: "btn-outline" },
			ghost: { button: "btn-ghost" },
			soft: { button: "btn-soft" },
			quiet: { button: "btn-quiet" },
			link: { button: "btn-link" },
			none: { button: "" },
		},
	},
	defaultVariants: {
		size: "medium",
		intentColor: "primary",
		typeVariant: "solid",
	},
});

function LanguageLabel({
	active,
	variant,
}: {
	active: LanguageDto;
	variant: LanguageTextLabelVariant;
}) {
	switch (variant) {
		case "none":
			return <span className="sr-only">{active.name}</span>;
		case "abbreviation":
			return (
				<>
					<span aria-hidden="true">{active.culture}</span>
					<span className="sr-only">{active.name}</span>
				</>
			);
		case "text":
			return <span aria-hidden="true">{active.name}</span>;
	}
}

function getCookie(name: string): string | undefined {
	if (typeof document === "undefined") return undefined;

	return document.cookie
		.split("; ")
		.find((cookie) => cookie.startsWith(`${name}=`))
		?.split("=")[1];
}

function deleteCookie(name: string) {
	// biome-ignore lint/suspicious/noDocumentCookie: The Cookie Store API lacks browser support needed here.
	document.cookie = `${name}=; Max-Age=0; path=/`;
}

export function LanguageSelector({
	languages,
	size = "medium",
	intentColor = "primary",
	typeVariant = "solid",
	textLabelVariant = "text",

	icon,
	iconPosition = "start",
	iconSize = "medium",
	className,
}: LanguageSelectorProps) {
	const currentLang = getLocale();
	const hydrated = useHydrated();
	const [restoringOriginal, setRestoringOriginal] = useState(false);
	const [activeCulture, setActiveCulture] = useState<string>(currentLang);
	const activeLanguage =
		languages.find((item) => item.culture === activeCulture) ?? languages[0];

	const { root, button, menu, item } = languageSelector({
		size,
		intentColor,
		typeVariant,
	});

	const iconElement = icon ? (
		<UmbSpriteIcon icon={icon} iconSize={iconSize} />
	) : null;

	const [requestAttempt, setRequestAttempt] = useState(0);
	const [loading, setLoading] = useState(false);
	const [translationError, setTranslationError] = useState(false);
	const [googleSelect, setGoogleSelect] = useState<HTMLSelectElement | null>(
		null,
	);
	const [pendingLanguage, setPendingLanguage] = useState<string>();
	const hasLanguages = languages.length > 0;

	function changeGoogleTranslateLanguage(selected: LanguageDto) {
		const language = selected.culture;
		trackLanguageSelection(document, language, selected.name);
		if (language === currentLang || language === "auto") {
			setRestoringOriginal(true);
			deleteCookie("googtrans");
			// Reload the original document instead of depending on vendor iframe IDs
			// or Razor's contentLang input, neither of which is a React contract.
			window.location.reload();
			return;
		}
		setPendingLanguage(language);
		setRequestAttempt((value) =>
			!value || translationError ? value + 1 : value,
		);
	}

	useEffect(() => {
		const cookieCulture = getCookie("googtrans")?.split("/")[2];
		const saved = languages.some(
			(language) => language.culture === cookieCulture,
		)
			? cookieCulture
			: undefined;
		setActiveCulture(saved ?? currentLang);
		if (saved && saved !== currentLang)
			setRequestAttempt((value) => value || 1);
		const synchronize = () => {
			const selected = selectedGoogleTranslateLanguage();
			if (languages.some((language) => language.culture === selected))
				setActiveCulture(selected ?? currentLang);
		};
		synchronize();
		document.addEventListener(GOOGLE_TRANSLATE_CHANGE_EVENT, synchronize);
		return () =>
			document.removeEventListener(GOOGLE_TRANSLATE_CHANGE_EVENT, synchronize);
	}, [currentLang, languages]);

	useEffect(() => {
		if (!requestAttempt || !hasLanguages) return;
		let mounted = true;
		setGoogleSelect(null);
		setLoading(true);
		setTranslationError(false);
		getGoogleTranslateSelect(currentLang)
			.then((select) => {
				if (mounted) {
					setGoogleSelect(select);
					setLoading(false);
				}
			})
			.catch(() => {
				if (mounted) {
					setTranslationError(true);
					setLoading(false);
				}
			});
		return () => {
			mounted = false;
		};
	}, [currentLang, requestAttempt, hasLanguages]);

	useEffect(() => {
		if (!pendingLanguage || !googleSelect) return;
		googleSelect.value = pendingLanguage;
		if (googleSelect.value === pendingLanguage) {
			setActiveCulture(pendingLanguage);
			googleSelect.dispatchEvent(new Event("change", { bubbles: true }));
		} else setTranslationError(true);
		setPendingLanguage(undefined);
	}, [pendingLanguage, googleSelect]);

	return languages.length === 0 ? null : (
		<div className={cn(root(), className, "notranslate")} aria-busy={loading}>
			<MenuTrigger
				onOpenChange={(open) => {
					if (!open) return;
					setRequestAttempt((value) =>
						!value || translationError ? value + 1 : value,
					);
				}}
			>
				<Button
					type="button"
					// SSR has no press handlers yet, and the departing document must
					// not accept another activation while its reload is pending.
					isDisabled={!hydrated || restoringOriginal}
					className={button()}
					aria-label={m.language_selector()}
				>
					{iconPosition === "start" ? iconElement : null}
					{activeLanguage ? (
						<LanguageLabel active={activeLanguage} variant={textLabelVariant} />
					) : null}
					{iconPosition === "end" ? iconElement : null}
				</Button>
				<Popover placement="bottom end" offset={4}>
					<Menu className={cn(menu(), "notranslate")}>
						{languages
							.filter((item) => item.culture !== activeLanguage?.culture)
							.map((language) => (
								<MenuItem
									onAction={() =>
										changeGoogleTranslateLanguage(language)
									}
									key={language.culture}
									aria-label={language.name}
									className={cn(item(), "capitalize")}
								>
									{language.name}
								</MenuItem>
							))}

						<Header className="dropdown-item pointer-events-none border-t">
							<div className="bg-primary text-bg-primary p-2 text-balance  w-full max-w-48 text-xs  rounded">
								<span>{m.google_translate_disclaimer()}</span>
							</div>
						</Header>
					</Menu>
				</Popover>
			</MenuTrigger>
			{translationError ? (
				<p role="alert">{m.google_translate_error()}</p>
			) : null}
		</div>
	);
}

export function PreviewLanguageSelector({
	size = "medium",
	intentColor = "primary",
	typeVariant = "solid",
	textLabelVariant = "text",

	icon,
	iconPosition = "start",
	iconSize = "medium",
	className,
}: LanguageSelectorProps) {
	const currentLang = getLocale();
	const languageNames = new Intl.DisplayNames([currentLang], {
		type: "language",
	});
	const { root, button } = languageSelector({
		size,
		intentColor,
		typeVariant,
	});

	const iconElement = icon ? (
		<UmbSpriteIcon icon={icon} iconSize={iconSize} />
	) : null;

	return (
		<div className={cn(root(), className, "notranslate")}>
			<MenuTrigger>
				<Button
					type="button"
					className={button()}
					aria-label={m.language_selector()}
				>
					{iconPosition === "start" ? iconElement : null}
					{currentLang ? (
						<LanguageLabel
							active={{
								culture: currentLang,
								name: languageNames.of(currentLang) ?? currentLang,
								isDefault: true,
							}}
							variant={textLabelVariant}
						/>
					) : null}
					{iconPosition === "end" ? iconElement : null}
				</Button>
			</MenuTrigger>
		</div>
	);
}
